import { useCallback, useEffect, useMemo, useState } from "react";
import api from "api/api";

// prop validations
import PropTypes from "prop-types";

// mui components
import Button from "@mui/material/Button";
import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, FormLabel, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// redux
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import {
    loadCohensDPerformed,
    loadCohensdCoreDataId,
    loadCohensdBackendVariables,
    loadCohensdProductCodes,
    resetCohensDStore,
} from "store/features/cohensDSlice";

// mui icons
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

// custom components
import Loader from "./Loader";
import SampleSizeWarning from "./SampleSizeWarning";

function CohensDButton({ analysisLevelData = {}, tabData = {}, setCohensDResult = () => {}, disabled = false }) {
    const theme = useTheme();
    const dispatch = useDispatch();

    // props data
    const { moduleName, dataMappingData, analysisTypeValue } = analysisLevelData;
    const { productsSecondaryDataState } = tabData;

    // redux store data
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const consumerFilters = useSelector((state) => state.consumerFilterStore.currentSelectedFilters);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const removedKpiStoreState = useSelector((state) => state.analysisKpiStore[`${moduleName}RemovedKpiList`]);
    const flatRemovedKpiList = useMemo(() => removedKpiStoreState.flatMap((item) => item.split(",")), [removedKpiStoreState]);

    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setIsDisabled] = useState(disabled);

    const [checkedProducts, setCheckedProducts] = useState([]);
    const [checkedVariables, setCheckedVariables] = useState([]);
    const [isCohensDDialogOpen, setIsCohensDDialogOpen] = useState(false);
    const [selectedProductCoreDataId, setSelectedProductCoreDataId] = useState(0);

    const categoriesToDisplay = useMemo(() => ["Fragrance (Secondary)", "Sensory & Application"], []);
    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = isHairCare || isSkinCare;

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    useEffect(() => {
        const vars = dataMappingData
            .map((macro) => {
                const validSubCategories = macro.displayLabels.filter((sub) => productsSecondaryDataState.some((product) => product.scores?.some((score) => score.backendVariable === sub.backendVariable && score.mean !== null && score.mean !== "")));

                return validSubCategories;
            })
            .flatMap((inner) =>
                inner.map((datum) => ({
                    ...datum,
                    checked: false,
                    disabled: flatRemovedKpiList.includes(datum.backendVariable),
                }))
            );
        setCheckedVariables(vars);
    }, [dataMappingData, productsSecondaryDataState, categoriesToDisplay, moduleName, flatRemovedKpiList]);

    useEffect(() => {
        if (analysisTypeValue.value !== "mean") {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [analysisTypeValue]);

    const hasLowSampleSize = useMemo(() => {
        return productsSecondaryDataState?.some((p) => Number(p?.sampleSize) > 0 && Number(p?.sampleSize) < 75);
    }, [productsSecondaryDataState]);

    const handleCohensDClick = () => {
        if (!isCohensDDialogOpen) {
            dispatch(resetCohensDStore());
            setCheckedProducts([]);
            setCheckedVariables((prev) =>
                prev.map((variable) => ({
                    ...variable,
                    checked: false,
                    disabled: flatRemovedKpiList.includes(variable.backendVariable),
                }))
            );
        }
        setIsCohensDDialogOpen(!isCohensDDialogOpen);
    };

    useEffect(() => {
        if (checkedProducts.length !== 0) {
            let id = checkedProducts[0].coreDataId;
            setSelectedProductCoreDataId(id);
            const backendVariables = [...new Set(checkedProducts.flatMap((item) => item.scores.map((score) => score.backendVariable)))];

            setCheckedVariables((prev) =>
                prev.map((variable) => {
                    const isRemoved = flatRemovedKpiList.includes(variable.backendVariable);
                    if (backendVariables.includes(variable.backendVariable) && !isRemoved) {
                        return { ...variable, disabled: false };
                    }
                    return { ...variable, disabled: true, checked: false };
                })
            );
        } else {
            setSelectedProductCoreDataId(0);
            setCheckedVariables((prev) =>
                prev.map((variable) => {
                    const isRemoved = flatRemovedKpiList.includes(variable.backendVariable);
                    return {
                        ...variable,
                        disabled: isRemoved,
                        checked: isRemoved ? false : variable.checked,
                    };
                })
            );
        }
    }, [checkedProducts, flatRemovedKpiList]);

    const handleInputChange = (e, name, product) => {
        if (name === "products") {
            if (e.target.checked) {
                setCheckedProducts((checkedItems) => {
                    const selectedProductsForTtest = [
                        ...checkedItems,
                        {
                            title: product.title,
                            coreDataId: product.coreDataId,
                            studyType: product.studyType,
                            studyTypeMethod: product.studyTypeMethod,
                            isCompetitorProduct: product.isCompetitorProduct,
                            scores: product.scores,
                            sampleSize: product.sampleSize,
                        },
                    ];

                    return selectedProductsForTtest;
                });
            } else {
                const filteredId = checkedProducts.filter((ele) => ele.title !== e.target.value);
                setCheckedProducts(filteredId);
            }
        } else if (name === "variables") {
            const updatedVariables = checkedVariables.map((variable) => {
                if (variable.backendVariable === product.backendVariable) {
                    return {
                        ...variable,
                        checked: e.target.checked,
                    };
                }
                return variable;
            });
            setCheckedVariables(updatedVariables);
        }
    };

    const handleSelectAllVariables = (e) => {
        setCheckedVariables((prev) =>
            prev.map((variable) => {
                if (!variable.disabled) {
                    return { ...variable, checked: e.target.checked };
                } else {
                    return { ...variable };
                }
            })
        );
    };

    const checkProductDisabled = (product) => {
        let disabled;
        if (productColumnVisibility[`checked--${product.title.split(" ").join("_")}__${product.coreDataId}`]) {
            disabled = true;
        } else if (product.sampleSize === 0) {
            disabled = true;
        } else if (selectedProductCoreDataId === 0 || product.coreDataId === selectedProductCoreDataId) {
            disabled = false;
        } else {
            disabled = true;
        }

        return Boolean(disabled);
    };

    const getProductLabel = (product) => {
        const proTitle = `${product.title}__${product.coreDataId}`;
        const proTestCode = productsTestCodeMapData[proTitle];
        const isHidden = productColumnVisibility[`checked--${product.title.split(" ").join("_")}__${product.coreDataId}`];
        const hasProTestCode = product.title.includes(proTestCode);
        const isDisabled = checkProductDisabled(product);

        const studyTitle = selectedStudy.find((datum) => datum.id === product.coreDataId)?.study_reference__title;

        let contentTitle = hasProTestCode ? product.title.split("/")[0] : product.title;

        return (
            <span>
                {contentTitle} /{" "}
                {isOralCare && (
                    <>
                        <span style={!isHidden && !isDisabled ? { color: "#6f42c1" } : { color: "#969696" }}>{proTestCode}</span> /
                    </>
                )}
                {studyTitle}
                {isHidden && <span> (Hidden by User)</span>}
            </span>
        );
    };

    const getSelectAllCheckeckedStatus = (group) => {
        return group.filter((p) => !checkProductDisabled(p)).length > 0 && group.filter((p) => !checkProductDisabled(p)).every((product) => checkedProducts.some((p) => p.title === product.title && p.coreDataId === product.coreDataId));
    };

    const getGroupDisabledStatus = (hasMultipleStudies, group) => {
        return hasMultipleStudies && checkedProducts.length > 0 && !checkedProducts.some((p) => group.some((g) => g.coreDataId === p.coreDataId));
    };

    const returnGroupData = (product, index, isGroupDisabled) => {
        const disabled = checkProductDisabled(product) || isGroupDisabled;
        const isChecked = checkedProducts.some((ele) => ele.title === product.title && ele.coreDataId === product.coreDataId);

        return (
            <FormControlLabel
                sx={{
                    "& .MuiTypography-root": {
                        fontSize: 12,
                        color: "#495057",
                        padding: 0,
                    },
                    borderRadius: "5px",
                    margin: "3px 0px",
                    padding: "0px",
                    color: isChecked ? theme.palette.primary.main : theme.palette.text.primary,
                    background: isChecked ? "linear-gradient(270deg, rgb(0 103 239 / 0%) 0%, rgb(0 56 139 / 9%) 78%)" : "none",
                }}
                control={
                    <Checkbox
                        sx={{
                            color: "#969696",
                            "& .MuiSvgIcon-root": {
                                fontSize: 16,
                            },
                            "& Mui-checked .MuiSvgIcon-root": {
                                fontSize: 16,
                                color: "#1976d2",
                            },
                            padding: "6px",
                        }}
                        checked={isChecked}
                        value={product.title}
                        disabled={disabled}
                        name={product.title}
                        onChange={(e) => handleInputChange(e, "products", product)}
                    />
                }
                label={getProductLabel(product)}
                key={index + 1}
            />
        );
    };

    const renderProducts = () => {
        const hasMultipleStudies = selectedStudy.length > 1;
        let productGroups = [];
        if (hasMultipleStudies) {
            const groupedProducts = {};
            productsSecondaryDataState.forEach((product) => {
                const studyId = product.coreDataId;
                if (!groupedProducts[studyId]) {
                    groupedProducts[studyId] = [];
                }
                groupedProducts[studyId].push(product);
            });
            productGroups = Object.values(groupedProducts);
        } else {
            productGroups = [productsSecondaryDataState];
        }

        return (
            <FormControl
                component="fieldset"
                variant="standard"
                sx={{ width: "100%" }}
            >
                <FormLabel
                    component="legend"
                    className="input-label-dialog mb-0"
                >
                    Please Select Benchmark Products to perform Cohens&apos; d
                </FormLabel>

                {productGroups.map((group, groupIndex) => {
                    const isGroupDisabled = getGroupDisabledStatus(hasMultipleStudies, group);

                    const areAllEnabledProductsSelected = getSelectAllCheckeckedStatus(group);

                    return (
                        <div
                            key={`group-${groupIndex}`}
                            style={{ marginBottom: "15px", marginLeft: "20px" }}
                        >
                            <FormControlLabel
                                sx={{
                                    width: "100%",
                                    "& .MuiTypography-root": {
                                        fontSize: 12,
                                        color: "#495057",
                                        fontWeight: "bold",
                                    },
                                    margin: "3px 0px",
                                    marginLeft: "-9px",
                                    borderRadius: "5px",
                                    background: areAllEnabledProductsSelected ? "linear-gradient(270deg, rgb(0 103 239 / 0%) 0%, rgb(0 56 139 / 9%) 78%)" : "none",
                                }}
                                control={
                                    <Checkbox
                                        sx={{
                                            color: isGroupDisabled ? "#ccc" : "#969696",
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 16,
                                            },
                                            "& Mui-checked .MuiSvgIcon-root": {
                                                fontSize: 16,
                                                color: "#1976d2",
                                            },
                                            padding: "6px",
                                        }}
                                        checked={areAllEnabledProductsSelected}
                                        onChange={() => {
                                            if (areAllEnabledProductsSelected) {
                                                const newCheckedProducts = checkedProducts.filter((p) => !group.some((g) => g.title === p.title && g.coreDataId === p.coreDataId));
                                                setCheckedProducts(newCheckedProducts);
                                            } else {
                                                const productsToAdd = group.filter((product) => !checkProductDisabled(product) && !checkedProducts.some((p) => p.title === product.title && p.coreDataId === product.coreDataId));
                                                const newCheckedProducts = [...checkedProducts, ...productsToAdd];
                                                setCheckedProducts(newCheckedProducts);
                                            }
                                        }}
                                        disabled={isGroupDisabled}
                                    />
                                }
                                label="Select All"
                            />

                            <FormGroup sx={{ width: "100%" }}>{group.map((product, index) => returnGroupData(product, index, isGroupDisabled))}</FormGroup>
                            {hasMultipleStudies && groupIndex < productGroups.length - 1 && (
                                <div
                                    style={{
                                        height: "1px",
                                        backgroundColor: "#e0e0e0",
                                        margin: "10px 0",
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </FormControl>
        );
    };

    const renderVariables = () => {
        const selectAllChecked = checkedVariables.length > 0 && checkedVariables.filter((v) => !v.disabled).every((v) => v.checked === true);

        return (
            <FormControl
                component="fieldset"
                variant="standard"
                sx={{ width: "100%" }}
            >
                <FormLabel
                    component="legend"
                    className="input-label-dialog mb-0"
                >
                    Please Select Backend Variable(s) to perform Cohens&apos; d
                </FormLabel>
                <div style={{ marginBottom: "15px", marginLeft: "20px" }}>
                    <FormControlLabel
                        sx={{
                            width: "100%",
                            "& .MuiTypography-root": {
                                fontSize: 12,
                                color: "#495057",
                                fontWeight: "bold",
                            },
                            margin: "3px 0px",
                            marginLeft: "-9px",
                            borderRadius: "5px",
                            background: selectAllChecked ? "linear-gradient(270deg, rgb(0 103 239 / 0%) 0%, rgb(0 56 139 / 9%) 78%)" : "none",
                        }}
                        control={
                            <Checkbox
                                sx={{
                                    color: "#969696",
                                    "& .MuiSvgIcon-root": {
                                        fontSize: 16,
                                    },
                                    "& Mui-checked .MuiSvgIcon-root": {
                                        fontSize: 16,
                                        color: "#1976d2",
                                    },
                                    padding: "6px",
                                }}
                                checked={selectAllChecked}
                                onChange={(e) => {
                                    handleSelectAllVariables(e);
                                }}
                            />
                        }
                        label="Select All"
                    />

                    <FormGroup sx={{ width: "100%" }}>
                        {checkedVariables.map((variable, index) => {
                            const isChecked = variable.checked;

                            return (
                                <FormControlLabel
                                    sx={{
                                        "& .MuiTypography-root": {
                                            fontSize: 12,
                                            color: "#495057",
                                            padding: 0,
                                        },
                                        borderRadius: "5px",
                                        margin: "3px 0px",
                                        padding: "0px",
                                        color: isChecked ? theme.palette.primary.main : theme.palette.text.primary,
                                        background: isChecked ? "linear-gradient(270deg, rgb(0 103 239 / 0%) 0%, rgb(0 56 139 / 9%) 78%)" : "none",
                                    }}
                                    control={
                                        <Checkbox
                                            sx={{
                                                color: "#969696",
                                                "& .MuiSvgIcon-root": {
                                                    fontSize: 16,
                                                },
                                                "& Mui-checked .MuiSvgIcon-root": {
                                                    fontSize: 16,
                                                    color: "#1976d2",
                                                },
                                                padding: "6px",
                                            }}
                                            checked={isChecked}
                                            value={variable.backendVariable}
                                            disabled={variable.disabled}
                                            name={variable.displayLabel}
                                            onChange={(e) => handleInputChange(e, "variables", variable)}
                                        />
                                    }
                                    label={variable.displayLabel}
                                    key={index + 1}
                                />
                            );
                        })}
                    </FormGroup>
                </div>
            </FormControl>
        );
    };

    const consumerFilterPayload = useCallback(() => {
        return Object.entries(consumerFilters)
            .map(([product_code, { filters }]) => {
                const result = {};
                const product = product_code.split("__")[0].replaceAll("_", " ");

                Object.entries(filters).forEach(([filterKey, values]) => {
                    const checkedList = values.filter((v) => v.checked);
                    const checkedValues = checkedList.map((v) => v.label);

                    if (checkedValues.length) {
                        result[filterKey] = checkedValues.join(", ");
                    }
                });

                return Object.keys(result).length ? { product, ...result } : null;
            })
            .filter(Boolean);
    }, [consumerFilters]);

    const handleCohensDSubmit = async () => {
        setIsLoading(true);
        try {
            const url = process.env.REACT_APP_SERVER_PATH + `/secondary-study-datas/statistical-cohens-d/`;
            const backendVariables = checkedVariables.filter((variable) => variable.checked).map((variable) => variable.backendVariable);
            const productCodes = checkedProducts.map((product) => product.title);
            const coreDataId = checkedProducts[0]?.coreDataId || null;
            
            const payload = {
                core_data_id: coreDataId,
                backend_variables: backendVariables,
                product_codes: productCodes,
                consumer_filter: consumerFilterPayload(),
                product_category: productCategoryId,
            };
            
            dispatch(loadCohensdCoreDataId(coreDataId));
            dispatch(loadCohensdBackendVariables(backendVariables));
            dispatch(loadCohensdProductCodes(productCodes));

            await api
                .post(url, payload)
                .then((res) => {
                    dispatch(loadCohensDPerformed(true));
                    setCohensDResult(res.data);
                    setIsLoading(false);
                    setIsCohensDDialogOpen(false); 
                })
                .catch((error) => {
                    dispatch(loadCohensDPerformed(false));
                    handleError("error", error);
                    setIsLoading(false);
                    setIsCohensDDialogOpen(false);
                });
        } catch (error) {
            dispatch(loadCohensDPerformed(false));
            handleError("error", error);
            setIsLoading(false);
            setIsCohensDDialogOpen(false);
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            <Button
                size="small"
                className={disabled || isDisabled ? "custom-btn me-4 bold-content disabled" : "custom-btn liner-light-blue-background me-4 bold-content"}
                variant="contained"
                onClick={handleCohensDClick}
                disabled={disabled || isDisabled}
            >
                Cohens D
            </Button>
            {isCohensDDialogOpen && (
                <Dialog
                    open={isCohensDDialogOpen}
                    onClose={handleCohensDClick}
                    fullWidth={true}
                    maxWidth="md"
                >
                    <DialogTitle
                        style={{
                            backgroundColor: "rgb(51, 94, 234,0.1)",
                            color: "rgb(51, 94, 234)",
                            fontSize: 15,
                            textAlign: "center",
                            padding: "10px 12px",
                        }}
                        id="alert-dialog-title"
                    >
                        Selecting Products for Cohen&apos;s D
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 2,
                                color: "rgb(51, 94, 234) !important",
                            }}
                            onClick={handleCohensDClick}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <div style={{ height: 400, marginLeft: "12px" }}>
                            <div className="mt-3">{renderProducts()}</div>
                            <div className="mt-3">{renderVariables()}</div>
                        </div>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            paddingBottom: "20px",
                        }}
                    >
                        <div
                            style={{
                                alignSelf: "flex-start",
                                margin: "16px 24px",
                            }}
                        >
                            {hasLowSampleSize && !isBeautyAndWellbeing && <SampleSizeWarning threshold={75} />}
                            <div style={{ display: "flex", paddingTop: "10px" }}>
                                <WarningAmberOutlinedIcon sx={{ color: "#FFC000", fontSize: 18 }} />
                                <p
                                    style={{
                                        color: "#495057",
                                        fontWeight: 600,
                                        fontSize: 12,
                                    }}
                                >
                                    Cohen&apos;s D is applied on products belonging to the same study reference
                                </p>
                            </div>
                        </div>
                        <Button
                            size="small"
                            className={!checkedVariables.some((datum) => datum.checked) ? "custom-btn  disabled" : "custom-btn custom-btn-outlined color-blue"}
                            variant="outlined"
                            disabled={!checkedVariables.some((datum) => datum.checked)}
                            onClick={handleCohensDSubmit}
                        >
                            Get Results
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

CohensDButton.propTypes = {
    analysisLevelData: PropTypes.object,
    tabData: PropTypes.shape({
        productsSecondaryDataState: PropTypes.array,
    }).isRequired,
    disabled: PropTypes.bool,
    setCohensDResult: PropTypes.func,
};

export default CohensDButton;